import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { Building2 } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { toast } from "sonner";

const OAUTH_ERRORS = {
  no_profile: "Your Arborvitae profile has not been set up yet. Please ask your admin to add you, then sign in again.",
  no_access: "Please sign in with your @arborvitae.in Google account.",
  domain_not_allowed: "Please sign in with your @arborvitae.in Google account.",
  provision_failed: "We couldn't set up your account. Please contact HR.",
  state_mismatch: "Sign-in failed (state mismatch). Please try again.",
  token_exchange_failed: "Sign-in failed. Please try again.",
  userinfo_failed: "Sign-in failed. Please try again.",
  missing_code: "Sign-in was cancelled.",
  oauth_exception: "Sign-in failed. Please try again.",
};

export default function LoginPage() {
  const { user } = useAuth();
  const [error, setError] = useState("");
  const loc = useLocation();
  const [sp] = useSearchParams();

  useEffect(() => {
    const err = sp.get("error");
    if (err) {
      const msg = OAUTH_ERRORS[err] || "Sign-in failed";
      setError(msg);
      toast.error(msg);
    }
  }, [sp]);

  if (user) return <Navigate to={loc.state?.from || "/"} replace />;

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-zinc-50">
      {/* Left: Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-white border-r border-zinc-200 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-70 pointer-events-none" />
        <div className="relative z-10">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-emerald-600 grid place-items-center">
              <Building2 className="h-5 w-5 text-white" strokeWidth={1.75} />
            </div>
            <span className="font-display text-xl font-bold tracking-tight text-zinc-900">Arborvitae HR</span>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-display text-4xl xl:text-5xl font-bold tracking-tight text-zinc-900 leading-[1.05]">
            Payroll, attendance & performance —
            <span className="text-emerald-600"> in one calm workspace.</span>
          </h1>
          <p className="mt-6 text-zinc-500 text-base leading-relaxed">
            Built for growing Indian teams. India-ready compliance, one-tap check-in, and a workflow that respects everyone's time.
          </p>
        </div>
        <div className="relative z-10 text-xs text-zinc-400">© {new Date().getFullYear()} Arborvitae</div>
      </div>

      {/* Right: sign-in */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-lg bg-emerald-600 grid place-items-center">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <span className="font-display text-xl font-bold text-zinc-900">Arborvitae HR</span>
          </div>

          <div className="mb-8">
            <h2 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Welcome back</h2>
            <p className="mt-2 text-sm text-zinc-500">Use your Arborvitae Google account to continue.</p>
          </div>

            {error ? (
              <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">{error}</div>
            ) : null}



          <a
            href={`${API_BASE}/auth/google/start`}
            className="mt-4 flex items-center justify-center gap-3 w-full h-11 rounded-md border border-zinc-300 bg-white hover:bg-zinc-50 text-sm font-medium text-zinc-800 press-scale"
            data-testid="google-signin-btn"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.7 1.22 9.19 3.61l6.85-6.85C35.9 2.36 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              <path fill="none" d="M0 0h48v48H0z"/>
            </svg>
            <span>Sign in with Google</span>
          </a>

        </div>
      </div>
    </div>
  );
}
